import { AlertCard, Card, Link, Table } from "@caprikorn/dynamo-ui";
import { type FC, useEffect } from "react";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { MSPRO_SUPPORT_EMAIL } from "@core/core.constants";
import { Download, FilePdf, FileXls, Redo } from "@icons";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useGetPerimeterExport } from "@services/apiEcms/useGetPerimeterExport";
import { useGetPerimeterExportDownload } from "@services/apiEcms/useGetPerimeterExportDownload";
import { usePostDocumentsGroupDownload } from "@services/apiEcms/usePostDocumentsGroupDownload";
import { usePostPerimeterExport } from "@services/apiEcms/usePostPerimeterExport";
import { CustomTrans } from "@utils/translations/CustomTrans";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { useDocumentColumns } from "./DocumentsTable.columns";
import { useDocumentDownloadAction } from "./DocumentsTable.hooks";
import classes from "./DocumentsTable.module.css";
import type { DocumentRow } from "./DocumentsTable.types";

export const DocumentsTable: FC = () => {
  const { t } = useCustomTranslation("main");
  const { freshdeskSupportUrl, supportEmail: defaultSupportEmail = "" } =
    useTenant();
  const { currentGroup } = useGroupReferences();
  const { data } = useGetGroup({
    groupId: currentGroup,
  });
  const groupName = data?.group.name ?? "";
  const supportEmail = data?.group.email || defaultSupportEmail;

  const { mutationStatus, formattedDocuments, isGetDocumentsError } =
    useDocumentDownloadAction(supportEmail);

  const {
    mutate: generatePerimeterExtract,
    isPending: isPerimeterExtractGenerationPending,
  } = usePostPerimeterExport(supportEmail);

  const {
    mutate: downloadPerimeterExtract,
    isPending: isPerimeterExtractDownloadPending,
  } = useGetPerimeterExportDownload({ groupName, supportEmail });

  const {
    data: perimeterExtract,
    isError: isPerimeterExtractError,
    refetch: perimeterExtractRefetch,
    isStale,
  } = useGetPerimeterExport({ isAdmin: data?.isAdmin });

  const {
    mutate: downloadDocument,
    isPending: isDownloadDocumentPending,
    variables: downloadDocumentVariables,
  } = usePostDocumentsGroupDownload({
    supportEmail,
  });

  useEffect(() => {
    if (isStale && perimeterExtract?.status === "IN_PROGRESS") {
      perimeterExtractRefetch();
    }
  }, [perimeterExtract?.status, perimeterExtractRefetch, isStale]);

  const isDataPartial =
    mutationStatus === 204 || isPerimeterExtractError || isGetDocumentsError;

  const perimeterExtractRow: DocumentRow = {
    name: t("documents.table.extractPerimeterName", { groupName }),
    status: perimeterExtract?.status,
  };

  const documentColumns = useDocumentColumns(perimeterExtract);

  const getTableData = () => {
    if (isDataPartial) {
      return perimeterExtract ? [perimeterExtractRow] : [];
    }

    return perimeterExtract
      ? [...formattedDocuments, perimeterExtractRow]
      : formattedDocuments;
  };

  const tableData: DocumentRow[] = getTableData();

  const getRowActions = (row: DocumentRow) => {
    // Row actions for documents other than perimeter exports
    if (!row.status) {
      return [
        {
          label: t("documents.table.rowActions.xlsDownload"),
          icon: FileXls,
          disabled:
            !row.xls ||
            (downloadDocumentVariables?.s3VersionID === row.xls?.s3VersionID &&
              isDownloadDocumentPending),
          onClick: () => {
            if (!row.xls) return;

            downloadDocument({
              fileName: row.xls.fileName,
              fileExtension: row.xls.fileExtension,
              s3VersionID: row.xls.s3VersionID,
            });
          },
          tooltip: t("documents.table.rowActions.xlsDownload"),
        },
        {
          label: t("documents.table.rowActions.pdfDownload"),
          icon: FilePdf,
          disabled:
            !row.pdf ||
            (downloadDocumentVariables?.s3VersionID === row.pdf?.s3VersionID &&
              isDownloadDocumentPending),
          onClick: () => {
            if (!row.pdf) return;

            downloadDocument({
              fileName: row.pdf.fileName,
              fileExtension: row.pdf.fileExtension,
              s3VersionID: row.pdf.s3VersionID,
            });
          },
          tooltip: t("documents.table.rowActions.pdfDownload"),
        },
      ];
    }

    if (row.status === "IN_PROGRESS") return [];

    if (row.status === "DONE") {
      return [
        {
          label: t("documents.table.rowActions.extractPerimeterDownload"),
          icon: Download,
          disabled: isPerimeterExtractDownloadPending,
          onClick: () => downloadPerimeterExtract(),
          tooltip: t("documents.table.rowActions.extractPerimeterDownload"),
        },
      ];
    }

    return [
      {
        label: t("documents.table.rowActions.extractPerimeterGeneration"),
        icon: Redo,
        disabled: isPerimeterExtractGenerationPending,
        onClick: () => generatePerimeterExtract(),
        tooltip: t("documents.table.rowActions.extractPerimeterGeneration"),
      },
    ];
  };

  return (
    <Card className={classes.container}>
      {isDataPartial && (
        <AlertCard size="sm" variant="info">
          <p>
            {supportEmail !== MSPRO_SUPPORT_EMAIL ? (
              <CustomTrans
                components={{
                  supportLink: <Link href={`mailto:${supportEmail}`} />,
                }}
                i18nKey="documents.contactEmail"
                ns="main"
                values={{ label: supportEmail }}
              />
            ) : (
              <CustomTrans
                components={{
                  supportLink: (
                    <Link
                      href={freshdeskSupportUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  ),
                }}
                i18nKey="documents.contactForm"
                ns="main"
              />
            )}
          </p>
        </AlertCard>
      )}
      <Table
        columns={documentColumns}
        data={tableData || []}
        noPagination
        rowActions={getRowActions}
      >
        <Table.Content />
      </Table>
    </Card>
  );
};
